import { Apple, ArrowLeft, Database, FileText, FolderOpen, Globe, Loader2, Monitor, Search, Settings, Shield, ShieldCheck, Square, User } from "lucide-react";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Terminal } from "react-feather";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "../../ui/button";
import StatusIndicator from "../Endpoints/StatusIndicator";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Device } from "../interfaces/EndpointInterfaces";
import { myLocalStorage } from "../../StorageHelper";
import useUserStore from "../../../services/userStore";
import { getUsers } from "../Resources/Api/resourceApi";
import useNotificationStore from "../../../services/notificationStore";
import { fetchResources } from "./Api/tenantuserendpointsapi";
import { Resource } from "./interface";
import TenantResourceRequest from "./TenantResourceRequest";
import { Skeleton } from "../interfaces/CommonInterfaces";
import { Avatar, AvatarFallback } from "../../ui/avatar";
import { labelMapping } from "../Resources/helper";
import { getOSIcon, transformApiToDevicePerDevice } from "../Endpoints/helper";
import EmptyStateCard from "../EmptyStateCard";
import { getComputersPerDevice } from "../Endpoints/Api/EndpointApi";



const tabs = [
    { id: "app", name: "Application control", icon: Settings },
    { id: "folder", name: "Data access", icon: Database },
    { id: "privilege", name: "Privilege management", icon: Shield },
    { id: "url", name: "URL control", icon: Globe }
];

const ITEMS_PER_PAGE = 10;

export default function TenantUserDeviceDetails() {
    const location = useLocation();
    const { deviceId } = useParams<{ deviceId: string }>();
    const [device, setDevice] = useState<Device | undefined>(location.state?.device);
    const [isDeviceLoading, setIsDeviceLoading] = useState(!location.state?.device);

    let tenant = myLocalStorage.getItem("latestTenant");

    const [activeTab, setActiveTab] = useState("app");
    const [allResources, setAllResources] = useState<Resource[]>([]);
    const [timers, setTimers] = useState<Record<string, number>>({});
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [showAccessDialog, setShowAccessDialog] = useState(false);
    const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
    const [users, setUsers] = useState<any[]>([]);
    const [isLoading, setLoading] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<any>();

    let tenantName = myLocalStorage.getItem("latestTenant")?.tenantName;
    let { email, role, userRole, userId } = useUserStore((state) => state.user);
    const navigate = useNavigate();
    const { userApprovedNotifications } =
        useNotificationStore();

    const isDeviceSSHBased = (device: Device) => {
        if (
            device?.OperatingSystem?.toLowerCase().includes("linux")
        )
            return true;
        else return false;
    };

    const getStatusVal = (resource: Resource) => {
        const statusValue =
            resource.status === "INHERITED"
                ? resource.inheritedGroupStatus
                : resource.status;

        if (statusValue === "DYNAMIC") return "TIMED";
        return statusValue;
    }

    const fetchUsers = useCallback(async (activeDevice: Device) => {
        try {
            let isOwnComputer = activeDevice?.belongedToUser === email;

            const response = await getUsers(
                email,
                tenantName,
                activeDevice.id,
                role,
                isOwnComputer,
                true,
                userRole,
                isDeviceSSHBased(activeDevice),
                userApprovedNotifications
            );

            if (response?.length) {
                const filteredUsers = response.filter((user: any) => user.cn !== "root" && user.cn !== "nobody");
                setUsers(filteredUsers)
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error("Failed to fetch users:", err);
            setUsers([]);
        }
    }, [email, tenantName, role, userRole, userApprovedNotifications]);


    const labelStatus = (mode: string, osmode: string) => {
        let status = mode
        if (mode === "INHERITED_FROM_PROFILE") status = osmode;
        if (mode === "INHERITED_FROM_PROFILE" && osmode === "INHERITED_FROM_TENANT") status = tenant.mode
        return status.toUpperCase();

    }

    const getOperatingModeColor = (mode: string, osMode: string) => {
        let status = labelStatus(mode, osMode);
        switch (status.toLowerCase()) {
            case "enforcing":
                return "destructive";
            case "auditing":
                return "default";
            case "disabled":
                return "secondary";
            default:
                return "secondary";
        }
    };

    useEffect(() => {
        if (!allResources) return;

        const initialTimers: Record<string, number> = {};

        allResources.forEach(resource => {
            const statusVal = getStatusVal(resource);
            const label = labelMapping(statusVal, "");

            if (
                resource.timedAccess?.durationSeconds &&
                (resource.timedAccess && resource.status === "DYNAMIC")
            ) {
                initialTimers[resource.id] = resource.timedAccess.durationSeconds;

            } else if (label && resource.timedAccess?.durationSeconds) {
                initialTimers[resource.id] = resource.timedAccess.durationSeconds;
            }
        });

        setTimers(initialTimers);
    }, [allResources, activeTab]);





    useEffect(() => {
        const interval = setInterval(() => {
            setTimers(prev => {
                const updated = { ...prev };
                let hasChanges = false;
                Object.keys(updated).forEach(resourceId => {
                    if (updated[resourceId] > 0) {
                        updated[resourceId] = updated[resourceId] - 1;
                        hasChanges = true;
                    }
                });

                return hasChanges ? updated : prev;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const loadDevice = async () => {
            if (device?.id) {
                setIsDeviceLoading(false);
                return;
            }
            if (!deviceId || !tenantName || !email || !role) {
                return;
            }
            setIsDeviceLoading(true);
            try {
                const response = await getComputersPerDevice({
                    tenantName,
                    email,
                    role,
                    userId,
                    id: deviceId,
                });
                if (response) {
                    setDevice(transformApiToDevicePerDevice(response));
                }
            } catch (error) {
                console.error("Failed to fetch device details:", error);
            } finally {
                setIsDeviceLoading(false);
            }
        };
        void loadDevice();
    }, [device?.id, deviceId, tenantName, email, role, userId]);

    useEffect(() => {
        if (!device?.id || !email || !role || !tenantName) {
            return;
        }
        void fetchUsers(device);
    }, [device?.id, email, role, tenantName, userApprovedNotifications, fetchUsers]);

    const handleTabChange = (tabId: string) => {
        setActiveTab(tabId);
        setCurrentPage(1);
    };

    const filteredResources = useMemo(() => {
        if (!searchTerm) return allResources;

        return allResources.filter(resource =>
            resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (resource.path && resource.path.toLowerCase().includes(searchTerm.toLowerCase()))
        );
    }, [allResources, searchTerm]);

    const paginatedResources = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredResources.slice(startIndex, endIndex);
    }, [filteredResources, currentPage]);

    const totalPages = Math.ceil(filteredResources.length / ITEMS_PER_PAGE);

    const handleSearchChange = (value: string) => {
        setSearchTerm(value);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handleRequestAccess = (resource: Resource) => {
        setSelectedResource(resource);
        setShowAccessDialog(true);
    };

    const getResourceIcon = (type: string) => {
        switch (type) {
            case "APP":
                return <FileText className="w-5 h-5 text-red-600" />;
            case "FOLDER":
                return <FolderOpen className="w-5 h-5 text-red-600" />;
            case "GROUP":
                return <Shield className="w-5 h-5 text-red-600" />;
            case "URL":
                return <Globe className="w-5 h-5 text-red-600" />;
            default:
                return <FileText className="w-5 h-5 text-red-600" />;
        }
    };

    const getDeviceInfo = () => {
        return { name: device?.dNSHostName || device?.name || "", status: "online", os: device?.OperatingSystem || device?.os || "" };
    };



    const deviceInfo = getDeviceInfo();

    const fetchResourceForTab = async (isFirst = false, activeTab: string) => {
        if (!device?.id || !selectedUser?.id) {
            return;
        }
        setLoading(isFirst);

        const data = await fetchResources(activeTab, tenantName, device.id, selectedUser.id);

        const sortedData = Array.isArray(data)
            ? [...data].sort((a, b) => (a.id > b.id ? 1 : a.id < b.id ? -1 : 0))
            : [];

        const seenPaths = new Set();
        const distinctData = sortedData.filter((item) => {
            if (seenPaths.has(item.path)) return false;
            seenPaths.add(item.path);
            return true;
        });

        setAllResources(distinctData);
        setLoading(false);
    };


    const formatTime = (seconds: any) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = seconds % 60;

        return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
    };


    useEffect(() => {
        if (selectedUser) {
            fetchResourceForTab(true, activeTab);
            const interval = setInterval(() => {
                fetchResourceForTab(false, activeTab);
            }, 10000)
            return () => clearInterval(interval)

        }

    }, [activeTab, selectedUser])

    useEffect(() => {
        if (users.length > 0) {
            setSelectedUser((prev: any) => {
                const stillValid = prev && users.some((user: any) => user.id === prev.id);
                return stillValid ? prev : users[0];
            });
        } else {
            setSelectedUser(undefined);
        }
    }, [users])

    if (isDeviceLoading || !device) {
        return (
            <div className="flex-1 flex items-center justify-center p-6">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
        );
    }


    return (
        <div className="flex-1 space-y-6 p-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate("/endpoints")}
                        className="gap-2"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back to Devices
                    </Button>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-8 h-8 ">
                            {getOSIcon(deviceInfo.os)}
                        </div>
                        <div>
                            <h1 className="text-xl font-semibold tracking-tight">{deviceInfo.name}</h1>
                            <div className="flex items-center gap-3">
                                <StatusIndicator lastSeen={"offline"} showDot isManualDevice={true} />
                                <span className="text-sm text-muted-foreground">{deviceInfo.os}</span>

                            </div>
                            <div className="flex items-center gap-2 mt-1 text-sm">
                                <Badge
                                    variant={getOperatingModeColor(device.mode || "", device.profileMode || "")}
                                    className="text-xs capitalize"
                                >
                                    {labelStatus(device.mode || "", device.profileMode || "")}
                                </Badge>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* Tabs */}
            <div className="h-full flex flex-col bg-white dark:bg-background border rounded-lg">
                <div className="flex border-b bg-white dark:bg-background">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => handleTabChange(tab.id)}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium ${activeTab === tab.id
                                ? "border-b-2 border-blue-500 text-blue-600 dark:text-blue-400"
                                : "text-muted-foreground"
                                }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.name}
                        </button>
                    ))}
                </div>
                {users.length > 0 && <div className="flex-1 bg-white dark:bg-background overflow-hidden">
                    <div className="h-full flex">
                        <div className="w-80 border-r bg-white dark:bg-background flex flex-col">
                            <div className="p-4 border-b">
                                <h3 className="text-sm font-medium text-muted-foreground mb-3">USER</h3>
                            </div>
                            <div className="flex-1 overflow-auto">
                                {users.map((user) => <div className="p-2">
                                    <div
                                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all duration-200
    ${selectedUser?.cn === user.cn
                                                ? "bg-blue-100 dark:bg-blue-900/40 border-blue-400 dark:border-blue-600"
                                                : ""
                                            }`}
                                        onClick={() => {
                                            setSelectedUser(user);
                                            setCurrentPage(1);
                                        }}
                                    >

                                        <div className="flex items-center gap-3 ">
                                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                                                <User className="w-4 h-4 text-muted-foreground" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-blue-900 dark:text-blue-100 truncate">
                                                    {user.cn}
                                                </div>
                                                <div className="text-xs text-blue-600 dark:text-blue-400">Active User</div>


                                            </div>

                                        </div>
                                    </div>
                                </div>)}

                            </div>
                        </div>
                        <div className="flex-1 bg-white dark:bg-background flex flex-col">
                            {/* Search */}
                            <div className="p-4 border-b space-y-4">
                                <div className="flex items-center justify-between">
                                    <h3 className="text-sm font-medium text-muted-foreground">
                                        DENIED RESOURCES ({filteredResources.length})
                                    </h3>
                                </div>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <Input
                                        placeholder={`Search ${tabs.find(t => t.id === activeTab)?.name.toLowerCase()}...`}
                                        value={searchTerm}
                                        onChange={(e) => handleSearchChange(e.target.value)}
                                        className="pl-10"
                                        data-testid="input-search-resources"
                                    />
                                </div>
                            </div>

                            {/* Resource List */}
                            <div className="flex-1 overflow-y-auto">
                                {isLoading ? <div className="divide-y">
                                    {[...Array(ITEMS_PER_PAGE)].map((_, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-4">
                                            <div className="flex items-center gap-3">
                                                <Skeleton className="w-6 h-6" />
                                                <Skeleton className="w-48 h-4" />
                                            </div>
                                            <div className="flex items-center gap-3 flex-shrink-0">
                                                <Skeleton className="w-24 h-4" />
                                            </div>
                                        </div>
                                    ))}
                                </div> : paginatedResources.length ? (
                                    <div className="divide-y">
                                        {paginatedResources.map((resource, index) => (
                                            <div key={`${resource.type}-${resource.id}-${index}`} className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800">
                                                <div className="flex items-start gap-3 flex-1 min-w-0">
                                                    <div className="flex-shrink-0 mt-0.5">
                                                        {getResourceIcon(resource.type)}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        {resource.type === "APP" && resource.path ? (
                                                            <div>
                                                                <div className="font-medium text-sm break-words">
                                                                    {resource.name}
                                                                </div>
                                                                <div className="text-xs text-muted-foreground font-mono break-all mt-1">
                                                                    {resource.path}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="font-medium text-sm break-words">
                                                                {resource.path || resource.name}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-3 flex-shrink-0">
                                                    <Badge
                                                        className="text-xs"
                                                        variant={
                                                            timers[resource.id] > 0 ? "secondary"
                                                                : labelMapping(getStatusVal(resource), "") === "ALLOWED"
                                                                    ? "default"
                                                                    : labelMapping(getStatusVal(resource), "") === "DENIED"
                                                                        ? "destructive"
                                                                        : labelMapping(getStatusVal(resource), "") === "TIMED"
                                                                            ? "secondary"
                                                                            : "default"
                                                        }
                                                    >

                                                        {timers[resource.id] > 0 ? "TIMED" : labelMapping(getStatusVal(resource), "")}
                                                    </Badge>

                                                    {timers[resource.id] > 0 && (
                                                        <span className="text-xs text-muted-foreground font-mono">
                                                            {formatTime(timers[resource.id])} left

                                                        </span>
                                                    )}

                                                    {(
                                                        labelMapping(getStatusVal(resource), "") === "DENIED" ||
                                                        (
                                                            labelMapping(getStatusVal(resource), "") === "TIMED" &&
                                                            (resource?.timedAccess?.durationSeconds ?? 0) === 0
                                                        )
                                                    ) && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="gap-2 border-blue-500 text-blue-600 hover:text-blue-700 hover:border-blue-600"
                                                                onClick={() => handleRequestAccess(resource)}
                                                            >
                                                                <ShieldCheck className="w-4 h-4" />
                                                                Request Access
                                                            </Button>
                                                        )}


                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="p-4">
                                        <EmptyStateCard
                                            compact
                                            icon={Shield}
                                            title="No resources found"
                                            description="No resources are available on this device for your role."
                                        />
                                    </div>
                                )}

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between p-4 border-t">
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                        >
                                            <ArrowLeft className="w-4 h-4" /> Previous
                                        </Button>
                                        <span className="text-sm text-muted-foreground">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next <ArrowLeft className="w-4 h-4 rotate-180" />
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>}

            </div>

            {showAccessDialog && selectedResource && (
                <TenantResourceRequest
                    open={showAccessDialog}
                    onOpenChange={setShowAccessDialog}
                    resourceName={selectedResource?.path || selectedResource?.name || "Resource"}
                    resourceType={selectedResource?.type || "Resource"}
                    deviceName={device.dNSHostName}
                    userId={selectedUser.id}
                    user={selectedUser}
                    resourceId={selectedResource?.id}
                    device={device}
                    publisher={selectedResource.publisher}
                />
            )}
        </div>
    );
}
